import React, { useState, useEffect } from 'react';
import { X, Shield } from 'lucide-react';
import { supabase, getApiBaseUrl } from '../../services/api';
import InfoModal from '../Modals/InfoModal';
import { useResetScrollAfterMobileKeyboard } from '../../hooks/useResetScrollAfterMobileKeyboard';
import './AuthModal.css';

const AuthModal = ({ initialView = 'log_in', onClose, onLoginSuccess, onSignUpSuccess, skipBodyScrollLock = false }) => {
  const [view, setView] = useState(initialView); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(null);

  useResetScrollAfterMobileKeyboard();

  useEffect(() => {
    if (skipBodyScrollLock) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [skipBodyScrollLock]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
  
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
  
    if (error) {
      if (error.message === 'Invalid login credentials') {
        try {
          const response = await fetch(`${getApiBaseUrl()}/auth/check-auth-method`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
          });
  
          const result = await response.json();
  
          if (!result.exists) {
            setError('No account exists with that email address.');
          } else if (result.hasOAuth && !result.hasPassword) {
            setError('Invalid login credentials. If you signed up with Google, Apple, Facebook, or X, please use that method to log in.');
          } else {
            setError('Invalid password.');
          }
        } catch (e) {
          console.error('Error checking auth method:', e);
          setError('Invalid login credentials.');
        }
      } else {
        setError(error.message);
      }
      setLoading(false);
    } else {
      if (window.handleSuccessfulLogin) {
        await window.handleSuccessfulLogin();
      }
      onLoginSuccess?.();
      onClose();
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
  
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
  
      if (error) throw error;
  
      if (data?.user?.identities?.length === 0) {
        setError('An account with this email address already exists. Please log in instead.');
        return;
      }
  
      setMessage('Check your email to confirm your account.');
      onSignUpSuccess?.();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
  
    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
  
      const result = await response.json();
        
      if (!response.ok) {
        setError('Unable to verify email. Please try again.');
        setLoading(false);
        return;
      }
  
      if (!result.exists) {
        setError('No account found with that email address.');
        setLoading(false);
        return;
      }
  
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
  
      if (error) {
        setError(error.message);
      } else {
        setMessage('Check your email for a password reset link.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('Something went wrong. Please try again.');
    }
    
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });

    if (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleFacebookSignIn = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
  
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: window.location.origin
      }
    });
  
    if (error) {
      setError(error.message);
      setLoading(false);
    }
  };
  
  const handleXSignIn = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
  
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'x',
      options: {
        redirectTo: window.location.origin
      }
    });
  
    if (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="auth-modal-logo">
          <div className="logo">
            <div className="logo-shield">
              <Shield size={50} />
              <span className="logo-text-k">K</span>
            </div>
            <span>RIME</span>
          </div>
        </div>

        {view === 'log_in' && (
          <div className="auth-modal-content">
            <h2>Welcome back</h2>
            <p className="auth-modal-subtitle">Log in with one of the following</p>

            <div className="sso-buttons-grid">
              <button className="sso-button" onClick={handleGoogleSignIn} disabled={loading}>
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google</span>
              </button>

              <button className="sso-button apple" disabled={loading}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span>Apple</span>
              </button>

              <button className="sso-button" onClick={handleFacebookSignIn} disabled={loading}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#1877f2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Facebook</span>
              </button>

              <button className="sso-button" onClick={handleXSignIn} disabled={loading}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span>X (Twitter)</span>
              </button>
            </div>

            <div className="divider">
              <span>or</span>
            </div>

            {error && <div className="auth-error">{error}</div>}
            {message && <div className="auth-message">{message}</div>}

            <form onSubmit={handleSignIn}>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="auth-input"
                autoComplete="email"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="auth-input"
                autoComplete="current-password"
              />
              <div className="forgot-password-container">
                <button 
                  type="button"
                  className="auth-link forgot-password-link" 
                  onClick={() => {
                    setView('forgot_password');
                    setError(null);
                    setMessage(null);
                  }}
                >
                  Forgot Password
                </button>
              </div>
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Logging in...' : 'Log in'}
              </button>
            </form>

            <div className="auth-terms">
              By logging in you agree to the{' '}
              <button type="button" className="auth-terms-link" onClick={() => setShowInfoModal('terms')}>
                Terms of Use
              </button>
              {' '}and{' '}
              <button type="button" className="auth-terms-link" onClick={() => setShowInfoModal('privacy')}>
                Privacy Policy
              </button>
            </div>

            <div className="auth-footer">
              Don't have an account?{' '}
              <button className="auth-link-bold" onClick={() => {
                setView('sign_up');
                setError(null);
                setMessage(null);
              }}>
                Sign up
              </button>
            </div>
          </div>
        )}

        {view === 'sign_up' && (
          <div className="auth-modal-content">
            <h2>Create your account</h2>
            <p className="auth-modal-subtitle">Sign up with one of the following</p>

            <div className="sso-buttons-grid">
              <button className="sso-button" onClick={handleGoogleSignIn} disabled={loading}>
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google</span>
              </button>

              <button className="sso-button apple" disabled={loading}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span>Apple</span>
              </button>

              <button className="sso-button" onClick={handleFacebookSignIn} disabled={loading}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#1877f2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Facebook</span>
              </button>

              <button className="sso-button" onClick={handleXSignIn} disabled={loading}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span>X (Twitter)</span>
              </button>
            </div>

            <div className="divider">
              <span>or</span>
            </div>

            {error && <div className="auth-error">{error}</div>}
            {message && <div className="auth-message">{message}</div>}

            <form onSubmit={handleSignUp}>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="auth-input"
                autoComplete="email"
              />
              <input
                type="password"
                placeholder="Password (min. 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="auth-input"
                autoComplete="new-password"
              />
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Creating account...' : 'Sign up'}
              </button>
            </form>

            <div className="auth-terms">
              By signing up you agree to the{' '}
              <button type="button" className="auth-terms-link" onClick={() => setShowInfoModal('terms')}>
                Terms of Use
              </button>
              {' '}and{' '}
              <button type="button" className="auth-terms-link" onClick={() => setShowInfoModal('privacy')}>
                Privacy Policy
              </button>
            </div>

            <div className="auth-footer">
              Already have an account?{' '}
              <button className="auth-link-bold" onClick={() => {
                setView('log_in');
                setError(null);
                setMessage(null);
              }}>
                Log in
              </button>
            </div>
          </div>
        )}

        {view === 'forgot_password' && (
          <div className="auth-modal-content">
            <h2>Reset your password</h2>
            
            {message ? (
              <>
                <div className="auth-success">
                  <div className="auth-message">{message}</div>
                  <button 
                    className="auth-link" 
                    onClick={() => {
                      setView('log_in');
                      setError(null);
                      setMessage(null);
                      setEmail('');
                    }}
                  >
                    Back to Log in
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="auth-modal-subtitle">Enter your email to receive a reset link</p>
                
                {error && <div className="auth-error">{error}</div>}
                
                <form onSubmit={handleForgotPassword}>
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="auth-input"
                    autoComplete="email"
                  />
                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Send reset link'}
                  </button>
                </form>
                <button 
                  className="auth-link" 
                  onClick={() => {
                    setView('log_in');
                    setError(null);
                    setMessage(null);
                  }}
                >
                  Back to Log in
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {showInfoModal && (
        <InfoModal
          type={showInfoModal}
          onClose={() => setShowInfoModal(null)}
        />
      )}
    </div>
  );
};

export default AuthModal;